import React, { useEffect, useRef } from 'react';
import {
  draggable,
  dropTargetForElements,
} from '@atlaskit/pragmatic-drag-and-drop/element/adapter';
import { Button } from '@/shared/ui/button';
import { Input } from '@/shared/ui/input';
import { ChevronDown, ChevronRight, GripVertical, Plus, Trash2 } from 'lucide-react';
import { ChecklistSection, ChecklistItem, FieldType } from '../types';
import ChecklistItemComponent from './ChecklistItem';

interface SectionProps {
  section: ChecklistSection;
  index: number;
  isNewlyCreated: boolean;
  newlyCreatedItemIds: Set<string>;
  onUpdate: (updates: Partial<ChecklistSection>) => void;
  onDelete: () => void | Promise<void>;
  onAddItem: (fieldType: FieldType) => void;
  onUpdateItem: (itemId: string, updates: Partial<ChecklistItem>) => void;
  onDeleteItem: (itemId: string) => void | Promise<void>;
}

const Section: React.FC<SectionProps> = ({
  section,
  index,
  isNewlyCreated,
  newlyCreatedItemIds,
  onUpdate,
  onDelete,
  onAddItem,
  onUpdateItem,
  onDeleteItem,
}) => {
  const sectionRef = useRef<HTMLDivElement>(null);
  const dragHandleRef = useRef<HTMLDivElement>(null);
  const itemsRef = useRef<HTMLDivElement>(null);

  const handleToggleCollapse = () => {
    onUpdate({ is_collapsed: !section.is_collapsed });
  };

  const handleTitleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    onUpdate({ title: e.target.value });
  };

  const handleAddItem = () => {
    onAddItem('Free Text');
  };

  // Set up draggable for the section (using drag handle)
  useEffect(() => {
    const element = dragHandleRef.current;
    if (!element) return;

    return draggable({
      element,
      getInitialData: () => ({
        type: 'section',
        sectionId: section.id,
        index,
      }),
    });
  }, [section.id, index]);

  // Set up drop target for section reordering
  useEffect(() => {
    const element = sectionRef.current;
    if (!element) return;

    return dropTargetForElements({
      element,
      getData: () => ({
        type: 'section-slot',
        sectionId: section.id,
        index,
      }),
      canDrop: ({ source }) => source.data.type === 'section',
    });
  }, [section.id, index]);

  // Set up drop target for items
  useEffect(() => {
    const element = itemsRef.current;
    if (!element) return;

    return dropTargetForElements({
      element,
      getData: () => ({
        type: 'items-container',
        sectionId: section.id,
        index: section.items.length,
      }),
      canDrop: ({ source }) => source.data.type === 'item',
    });
  }, [section.id, section.items.length]);

  return (
    <div
      ref={sectionRef}
      className="bg-white rounded-lg border transition-all shadow-sm hover:shadow-md group"
      data-section-drop-zone
    >
      {/* Section Header */}
      <div className="p-3 sm:p-4 border-b bg-gray-50 rounded-t-lg">
        <div className="flex items-center gap-2 sm:gap-3">
          <div
            ref={dragHandleRef}
            className="text-gray-400 hover:text-gray-600 cursor-grab active:cursor-grabbing hidden sm:block"
          >
            <GripVertical className="h-5 w-5" />
          </div>

          <Button
            variant="ghost"
            size="sm"
            onClick={handleToggleCollapse}
            className="p-1 h-auto shrink-0"
          >
            {section.is_collapsed ? (
              <ChevronRight className="h-4 w-4" />
            ) : (
              <ChevronDown className="h-4 w-4" />
            )}
          </Button>

          <div className="flex-1 min-w-0">
            <Input
              value={section.title}
              onChange={handleTitleChange}
              placeholder="Section title..."
              className={`border-none bg-transparent p-0 text-base sm:text-lg font-semibold focus-visible:ring-0 focus-visible:ring-offset-0 ${
                isNewlyCreated && !section.title.trim()
                  ? 'bg-orange-50 border-orange-200 placeholder:text-orange-400'
                  : ''
              }`}
            />
          </div>

          <div className="flex items-center gap-1 sm:gap-2 shrink-0">
            <Button
              variant="ghost"
              size="sm"
              onClick={handleAddItem}
              className="text-blue-600 hover:text-blue-700 hover:bg-blue-50 px-2 sm:px-3"
            >
              <Plus className="h-4 w-4 sm:mr-1" />
              <span className="hidden sm:inline">Add Item</span>
            </Button>

            <Button
              variant="ghost"
              size="sm"
              onClick={onDelete}
              className="text-red-600 hover:text-red-700 hover:bg-red-50 px-2 sm:px-3"
            >
              <Trash2 className="h-4 w-4" />
            </Button>
          </div>
        </div>
      </div>

      {/* Section Content */}
      {!section.is_collapsed && (
        <div className="p-3 sm:p-4">
          <div
            ref={itemsRef}
            className="space-y-3 sm:space-y-4 min-h-[40px] rounded-lg transition-all duration-200 ease-in-out"
          >
            {section.items.map((item, itemIndex) => (
              <div key={item.id} className="transition-all duration-200 ease-in-out">
                <ChecklistItemComponent
                  item={item}
                  index={itemIndex}
                  sectionId={section.id}
                  isNewlyCreated={newlyCreatedItemIds.has(item.id)}
                  onUpdate={(updates) => onUpdateItem(item.id, updates)}
                  onDelete={() => onDeleteItem(item.id)}
                />
              </div>
            ))}

            {section.items.length === 0 && (
              <div className="text-center py-6 sm:py-8 text-gray-500 border-2 border-dashed border-gray-200 rounded-lg">
                <p className="mb-2 text-sm sm:text-base">No items in this section yet.</p>
                <Button onClick={handleAddItem} variant="outline" size="sm">
                  <Plus className="h-4 w-4 mr-1" />
                  Add First Item
                </Button>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default Section;
